import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PLAN_FIELDS,
  PLAN_FIELD_LABELS,
  cellIsEmpty,
  cellMode,
  WEEKDAY_LABELS,
  eventColor,
  minutesRangeLabel,
  canPrintInBrowser,
  periodOf,
  standardCodesLabel,
  stripHtml,
  studentName,
  toDateKey,
  MAX_STICKIES,
  STICKY_COLORS,
  STICKY_SIZE,
  type DashboardResponse,
  type Todo,
} from '@td/shared';
import { useAuth } from '../auth/AuthContext';
import { get } from '../lib/api';
import { greeting, longDate, zonedNow } from '../lib/dates';
import { useCreateTodo, useDeleteTodo, useStopRepeating, useUpdateTodo } from '../hooks/useTodos';
import { useMinuteTick } from '../hooks/useClock';
import { useJobBoard } from '../hooks/useJobs';
import { documentDownloadUrl, documentUrl, useDocuments } from '../hooks/useDocuments';
import { printDocuments } from '../lib/printFile';
import { useStudents } from '../hooks/useStudents';
import { BookmarkTile } from '../components/BookmarkTile';
import { HabitTracker } from '../components/HabitTracker';
import { WidgetFrame } from '../components/WidgetFrame';
import { Icon, navColors, type NavColor } from '../components/Sidebar';
import { Doodle, EmptyMark, type DoodleName } from '../components/Doodle';
import { InboxWidget } from '../components/InboxWidget';
import { StickyLayer } from '../components/StickyBoard';
import { useCreateSticky, useStickyNotes } from '../hooks/useSticky';
import { Portal } from '../components/Portal';
import { WeatherWidget } from '../components/WeatherWidget';
import { gridLayout, pullUpFlush, resolveOverlaps } from '../lib/packCards';
import { maxHeightAt, maxWidthAt, resolveCollision, type Rect } from '../lib/placeCard';

/**
 * The widgets, in the order they ship.
 *
 * The arrangement is per device rather than per account: the laptop on the
 * desk and the tablet carried round the room are used for different parts of
 * the day, and a teacher shouldn't have to pick one arrangement for both.
 */
const WIDGETS = [
  'plan',
  'events',
  'todos',
  'habits',
  'inbox',
  'weather',
  'weather2',
  'weather3',
  'data',
  'links',
  'jobs',
  'documents',
] as const;
type WidgetKey = (typeof WIDGETS)[number];

const WIDGET_LABELS: Record<WidgetKey, string> = {
  plan: "Today's lesson plan",
  events: "Today's events",
  todos: "Today's to-do",
  habits: 'Habit tracker',
  inbox: 'Inbox',
  weather: 'Weather',
  weather2: 'Weather · second place',
  weather3: 'Weather · third place',
  data: 'Assessment progress',
  links: 'Quick links',
  jobs: 'Classroom jobs',
  documents: 'Pinned documents',
};

/*
 * Cards that exist but are put away until asked for.
 *
 * Three weather slots rather than one, because two towns side by side is a
 * reasonable thing to want and the alternative — one card cycling between
 * them — is not the same as seeing both at once. Two of them start hidden so
 * nobody who wanted one forecast gets three.
 */
const DEFAULT_HIDDEN: WidgetKey[] = ['weather2', 'weather3'];

const LAYOUT_KEY = 'td.dashboardLayout';

/**
 * Where each card sits, like a note pinned to a board.
 *
 * Positions are absolute rather than a running order, because that is what
 * "put it there and leave it" requires. Kept on the device rather than the
 * server: a layout that suits a classroom monitor is wrong on a laptop, and
 * syncing it would mean one of the two is always wrong.
 */
interface CardState {
  x: number;
  y: number;
  width: number;
  /** Null means as tall as its contents. */
  height: number | null;
  hidden: boolean;
  /** A locked card cannot be dragged or resized, even while arranging. */
  locked: boolean;
}

interface Layout {
  version: 2;
  cards: Record<WidgetKey, CardState>;
  /** Which card is drawn on top, most recently touched last. */
  stack: WidgetKey[];
}

/**
 * How much of the row a card takes before anyone has resized it. The plan and
 * the habit grid are wide by nature; the rest read fine narrow.
 */
/**
 * How many grid columns each card wants.
 *
 * A fraction of the screen was the old measure, and it is the wrong one: two
 * cards at a third each plus a gap do not add up to anything that lines up
 * with a card at a half. Columns do.
 */
const DEFAULT_SPAN: Record<WidgetKey, number> = {
  plan: 2,
  events: 1,
  todos: 1,
  habits: 2,
  inbox: 2,
  weather: 1,
  weather2: 1,
  weather3: 1,
  data: 1,
  links: 1,
  jobs: 1,
  documents: 1,
};

const GAP = 20;

/**
 * A line and a mark for each day.
 *
 * Small, but it is the first thing on screen at seven in the morning, and a
 * dashboard that acknowledges it is Friday costs nothing and reads as though
 * somebody thought about the person using it.
 */
const DAY_LINE = [
  'a quiet one, hopefully',
  'fresh week, fresh start',
  'well into it now',
  'over the hump',
  'nearly there',
  'last push — it is Friday',
  'the weekend is yours',
];

/**
 * Each card wears the colour of the page it belongs to, so the sidebar and
 * the dashboard agree about what is what. The habit tracker has no page of
 * its own, so it borrows the dashboard's own hue.
 */
/**
 * Each card wears the colour of the page it belongs to, so the sidebar and
 * the dashboard agree about what is what. A function rather than a constant,
 * because the palette changes with the theme.
 */
function widgetColors(): Record<WidgetKey, NavColor> {
  const nav = navColors();

  return {
    plan: nav['/lesson-plans']!,
    events: nav['/calendar']!,
    todos: nav['/todos']!,
    habits: nav['/']!,
    inbox: nav['/notes']!,
    weather: nav['/seating']!,
    weather2: nav['/calendar']!,
    weather3: nav['/data']!,
    data: nav['/data']!,
    links: nav['/bookmarks']!,
    jobs: nav['/jobs']!,
    documents: nav['/documents']!,
  };
}

/**
 * A tidy arrangement: even columns, even gaps, sized to the screen.
 *
 * Also exactly what "Tidy up" does. Free placement is the point of this
 * board, but everyone wants a way back to something neat after half an hour
 * of dragging — and the old version kept every card's hand-dragged width,
 * which meant "tidy" produced something as ragged as what it started from,
 * with cards landing on top of each other wherever a measured height was
 * missing.
 *
 * Two things fix that. Widths are rebuilt from a column grid rather than
 * carried over, and the height each card is packed with is the height it will
 * actually be drawn at — a card resized by hand keeps that height, so packing
 * with its content height was guaranteed to overlap whatever came next.
 */
function tidyLayout(
  width: number,
  heights: Partial<Record<WidgetKey, number>>,
  previous?: Layout,
): Layout {
  const order = previous ? [...previous.stack] : [...WIDGETS];
  const shown = order.filter((w) => !previous?.cards[w]?.hidden);

  const { placements, columnWidth } = gridLayout(
    shown.map((w) => ({
      key: w,
      span: DEFAULT_SPAN[w],
      // The drawn height, not the content height.
      height: previous?.cards[w]?.height ?? heights[w] ?? 240,
    })),
    width,
    GAP,
  );

  const cards = Object.fromEntries(
    WIDGETS.map((w) => {
      const at = placements.find((p) => p.key === w);
      const was = previous?.cards[w];
      return [
        w,
        {
          x: at?.x ?? 0,
          y: at?.y ?? 0,
          width: at?.width ?? Math.max(240, Math.round(columnWidth)),
          height: was?.height ?? null,
          hidden: was?.hidden ?? DEFAULT_HIDDEN.includes(w),
          locked: was?.locked ?? false,
        },
      ];
    }),
  ) as Record<WidgetKey, CardState>;

  return { version: 2, cards, stack: [...WIDGETS] };
}

function readLayout(): Layout | null {
  try {
    const raw = JSON.parse(localStorage.getItem(LAYOUT_KEY) ?? 'null') as
      (Partial<Layout> & { order?: WidgetKey[] }) | null;

    // Version 1 stored an order and some widths, with positions decided by
    // the layout engine. There is no sensible translation into free
    // coordinates without knowing how tall everything is, so it is dropped
    // and the board is laid out fresh — once, on one device.
    if (!raw || raw.version !== 2 || !raw.cards) return null;

    const cards = Object.fromEntries(
      WIDGETS.map((w) => {
        const saved = raw.cards![w];
        return [
          w,
          {
            x: Math.max(0, saved?.x ?? 0),
            y: Math.max(0, saved?.y ?? 0),
            width: Math.max(240, saved?.width ?? 320),
            height: saved?.height ?? null,
            // A card added to the app since this board was saved follows the
            // default, so three weather slots don't appear unasked for.
            hidden: saved?.hidden ?? DEFAULT_HIDDEN.includes(w),
            locked: saved?.locked ?? false,
          },
        ];
      }),
    ) as Record<WidgetKey, CardState>;

    // A card added to the app since this was saved appears rather than
    // vanishing; one that no longer exists is dropped.
    const stack = (raw.stack ?? []).filter((w) => WIDGETS.includes(w));
    return {
      version: 2,
      cards,
      stack: [...stack, ...WIDGETS.filter((w) => !stack.includes(w))],
    };
  } catch {
    return null;
  }
}

export function DashboardPage() {
  const { user } = useAuth();
  const firstName = user?.displayName?.split(' ')[0] ?? 'there';

  // The teacher's own date — the server would otherwise resolve "today" in
  // the container's timezone, which is UTC on Railway, and the device's own
  // clock is wrong for anyone working away from home.
  /*
   * Ticks with the clock.
   *
   * Without it the greeting still says "Good morning" at two in the
   * afternoon, and — the part that actually matters — a dashboard left open
   * overnight goes on showing yesterday's plan and yesterday's to-dos,
   * because `today` is only recomputed when something else causes a render.
   */
  useMinuteTick();
  const today = toDateKey(zonedNow());

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    // The date is part of the key. It is part of the request, so a cache
    // entry from yesterday is a different thing, not a stale version of this
    // one — and at midnight the board should reload rather than reuse it.
    queryKey: ['dashboard', today],
    queryFn: () => get<DashboardResponse>(`/dashboard?date=${today}`),
    /*
     * Most of what is on this board arrives through the live stream the
     * moment it changes. This is the safety net for the things that change
     * without anybody pressing anything — a lesson plan edited on a phone
     * while the stream was down, the district calendar, the day itself.
     */
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  /*
   * The year ended on this request. Everything the roll-over touched is now
   * stale in the cache — including the signed-in user, which is what raises
   * the prompt asking for the new year's dates.
   */
  const rolledOver = data?.rolledOver ?? false;
  useEffect(() => {
    if (!rolledOver) return;

    for (const key of [
      ['auth', 'me'],
      ['students'],
      ['assessments'],
      ['seating'],
      ['school-years'],
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  }, [rolledOver, queryClient]);

  // Read once. A lazy initialiser, or localStorage is parsed on every render.
  const [saved] = useState(readLayout);
  const [layout, setLayout] = useState<Layout | null>(saved);
  const [arranging, setArranging] = useState(false);

  // Sticky notes float over the whole board rather than living in a card, so
  // the page owns them: the button is up here and the layer is down there.
  const { data: notes = [] } = useStickyNotes();
  const createSticky = useCreateSticky();

  /*
   * Whether this device already had a board saved.
   *
   * A brand new one is laid out twice: once immediately with estimated
   * heights so there is something to look at and — crucially — something to
   * measure, then once more the moment every card has reported its real
   * height. Without the second pass the first arrangement is built on
   * guesses, and cards would overlap on the very first visit.
   */
  const restored = useRef(saved !== null);
  const settled = useRef(false);

  /*
   * The canvas width, measured rather than assumed, so a card can never be
   * dragged off the right-hand edge of the board.
   *
   * A callback ref, not an effect on mount. The canvas is only rendered once
   * the dashboard data has loaded, so an effect with an empty dependency list
   * runs while the node does not exist yet, finds nothing, and never runs
   * again — leaving the width stuck at its fallback and every card penned
   * into the left 1024 pixels of the screen. This attaches the moment the
   * node appears, whenever that is.
   */
  const [canvasWidth, setCanvasWidth] = useState(1024);
  const canvasObserver = useRef<ResizeObserver | null>(null);

  /*
   * The node itself, kept alongside the callback ref.
   *
   * `canvasRef` is a callback rather than a ref object — it has to be, to
   * attach the ResizeObserver the moment the node appears — and a callback
   * ref has no `.current`. Adding a note needs the board's position on
   * screen, so the node is stashed here as it arrives.
   */
  const canvasNode = useRef<HTMLDivElement | null>(null);

  const canvasRef = useCallback((node: HTMLDivElement | null) => {
    canvasObserver.current?.disconnect();
    canvasObserver.current = null;
    canvasNode.current = node;
    if (!node) return;

    setCanvasWidth(node.clientWidth);
    const observer = new ResizeObserver(() => setCanvasWidth(node.clientWidth));
    observer.observe(node);
    canvasObserver.current = observer;
  }, []);

  /*
   * Each card's height on screen, border and all.
   *
   * Reported by the card frame rather than by its contents. Measuring the
   * contents and adding a constant for the padding was the bug behind "Tidy
   * up" stacking cards on top of each other: the constant was wrong, and it
   * was wrong by a different amount while arranging, when the frame grows a
   * strip along the top for the drag handle.
   */
  const [heights, setHeights] = useState<Partial<Record<WidgetKey, number>>>({});

  // The same numbers, readable from a timer without re-arming an effect.
  const heightsRef = useRef(heights);
  heightsRef.current = heights;

  const measureCard = useCallback((widget: WidgetKey, height: number) => {
    setHeights((current) =>
      // Sub-pixel jitter would otherwise re-render on every animation frame.
      Math.abs((current[widget] ?? 0) - height) < 1 ? current : { ...current, [widget]: height },
    );
  }, []);

  // First run on this device: something to look at, and to measure.
  useEffect(() => {
    if (!layout && canvasWidth > 0) setLayout(tidyLayout(canvasWidth, heights));
  }, [layout, canvasWidth, heights]);

  useEffect(() => {
    if (!layout) return;
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
    } catch {
      // Not remembered; the arrangement still holds for this session.
    }
  }, [layout]);

  /**
   * Tidy up, then tidy up again once the cards have re-measured.
   *
   * One pass cannot be right. Laying the board out changes how wide every
   * card is, which changes how tall its contents are — a narrower inbox wraps
   * onto more lines — so the second pass is what packs the heights the first
   * pass caused. Two settle it; a third is there for the rare card whose
   * contents reflow again, and after that it is left alone rather than
   * looping forever.
   */
  const tidyPass = useRef<number | null>(null);

  const tidyUp = useCallback(() => {
    if (canvasWidth <= 0) return;

    let pass = 0;
    const run = () => {
      setLayout((l) => tidyLayout(canvasWidth, heightsRef.current, l ?? undefined));
      // Measurement lands a frame or two later; re-packing before it does
      // would use the very heights we are trying to replace.
      if (++pass < 3) tidyPass.current = window.setTimeout(run, 140);
    };

    if (tidyPass.current) window.clearTimeout(tidyPass.current);
    run();
  }, [canvasWidth]);

  useEffect(
    () => () => {
      if (tidyPass.current) window.clearTimeout(tidyPass.current);
    },
    [],
  );

  /** A card as a rectangle, using its natural height when it has no fixed one. */
  const rectOf = useCallback(
    (l: Layout, w: WidgetKey): Rect => ({
      x: l.cards[w].x,
      y: l.cards[w].y,
      width: l.cards[w].width,
      height: l.cards[w].height ?? heights[w] ?? 240,
    }),
    [heights],
  );

  function update(widget: WidgetKey, changes: Partial<CardState>) {
    setLayout((l) => {
      if (!l) return l;

      const next = { ...l.cards[widget], ...changes };

      /*
       * Two cards may not occupy the same space. If a drop or a resize would
       * overlap something, this card moves to the nearest spot that doesn't —
       * and nothing else moves, because the other cards are where they were
       * deliberately put.
       */
      const moved = changes.x !== undefined || changes.y !== undefined;
      const resized = changes.width !== undefined || changes.height !== undefined;

      if ((moved || resized) && !next.hidden) {
        const others = WIDGETS.filter((w) => w !== widget && !l.cards[w].hidden).map((w) =>
          rectOf(l, w),
        );

        const rect: Rect = {
          x: next.x,
          y: next.y,
          width: next.width,
          height: next.height ?? heights[widget] ?? 240,
        };

        if (resized) {
          next.width = Math.min(next.width, maxWidthAt(rect, others, canvasWidth, GAP));
          if (next.height !== null) {
            next.height = Math.min(next.height, maxHeightAt(rect, others, GAP));
          }
          rect.width = next.width;
          rect.height = next.height ?? rect.height;
        }

        const at = resolveCollision(rect, others, canvasWidth, GAP);
        next.x = at.x;
        next.y = at.y;
      }

      return { ...l, cards: { ...l.cards, [widget]: next } };
    });
  }

  /** Whatever was touched last is drawn on top of whatever it overlaps. */
  function raise(widget: WidgetKey) {
    setLayout((l) => (l ? { ...l, stack: [...l.stack.filter((w) => w !== widget), widget] } : l));
  }

  const visible = layout ? WIDGETS.filter((w) => !layout.cards[w].hidden) : [];
  const hidden = layout ? WIDGETS.filter((w) => layout.cards[w].hidden) : [];

  // The second pass, once the real heights are in. Only ever on a device that
  // had no saved board, and only once.
  useEffect(() => {
    if (!layout || restored.current || settled.current || canvasWidth <= 0) return;
    if (visible.length === 0 || !visible.every((w) => heights[w] !== undefined)) return;

    settled.current = true;
    setLayout((l) => tidyLayout(canvasWidth, heights, l ?? undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, canvasWidth, heights, visible.join(',')]);

  /*
   * Nothing may be drawn on top of anything else.
   *
   * Two ways that happens. A card grows while you are looking at it — add a
   * to-do and it gets taller — and a card is already the wrong size the
   * moment it appears, which is what happens when the to-dos were added on
   * another computer and this one is loading an arrangement saved before
   * they existed. The second case has no growth to watch for, which is
   * exactly why watching for growth was not enough.
   *
   * So this does not watch for anything: whenever the measurements change it
   * asks whether the board is legal, and if it is not, cards move down until
   * it is. When nothing overlaps it does nothing at all, which is almost
   * always.
   */
  const lastHeights = useRef<Partial<Record<WidgetKey, number>>>({});

  useEffect(() => {
    if (!layout || arranging) {
      // Entering arrange mode grows every card by the height of its drag
      // strip. That is not a card changing size, so it must not be read as
      // one on the way back out.
      if (arranging) lastHeights.current = { ...heights };
      return;
    }

    // Every visible card has to have reported before this means anything; a
    // card still measuring counts as zero and would drag the board up.
    if (visible.length === 0 || !visible.every((w) => heights[w] !== undefined)) return;

    const previous = lastHeights.current;
    lastHeights.current = { ...heights };

    setLayout((l) => {
      if (!l) return l;

      const rectsOf = (cards: Layout['cards']) =>
        WIDGETS.filter((w) => !cards[w].hidden).map((w) => ({
          key: w,
          x: cards[w].x,
          y: cards[w].y,
          width: cards[w].width,
          height: cards[w].height ?? heights[w] ?? 240,
        }));

      let cards = l.cards;

      /*
       * Shrinking first, then overlaps.
       *
       * Tick the last to-do off and the card gets shorter; whatever was
       * resting on it comes up to meet it. That has to happen before the
       * overlap pass, which only ever moves things down and would happily
       * declare the resulting gap perfectly legal.
       */
      for (const w of WIDGETS) {
        const was = previous[w];
        const now = heights[w];
        if (was === undefined || now === undefined || cards[w].hidden) continue;
        // A card with a hand-set height does not shrink with its contents.
        if (cards[w].height !== null) continue;
        if (was - now <= 0.5) continue;

        const moved = pullUpFlush(rectsOf(cards), w, was, GAP);
        if (Object.keys(moved).length === 0) continue;

        cards = { ...cards };
        for (const [key, y] of Object.entries(moved)) {
          cards[key as WidgetKey] = { ...cards[key as WidgetKey], y: Math.max(0, y) };
        }
      }

      const moved = resolveOverlaps(rectsOf(cards), GAP);
      if (Object.keys(moved).length > 0) {
        cards = { ...cards };
        for (const [w, y] of Object.entries(moved)) {
          cards[w as WidgetKey] = { ...cards[w as WidgetKey], y };
        }
      }

      return cards === l.cards ? l : { ...l, cards };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heights, layout, arranging, visible.join(',')]);

  /*
   * How far the board scrolls: the bottom of the lowest card, plus room to
   * drag something below it. Without the slack there would be no empty space
   * to move a card into.
   */
  /*
   * A new note lands in the middle of what she is looking at.
   *
   * Not the top left corner of the board: the point of these is that they go
   * on top of something, and the middle of the visible board is the nearest
   * thing to "where she is". She drags it the last few inches herself.
   */
  async function addSticky() {
    const box = canvasNode.current?.getBoundingClientRect();

    const middle = box
      ? {
          x: Math.max(0, window.innerWidth / 2 - box.left - STICKY_SIZE.width / 2),
          y: Math.max(0, window.innerHeight / 2 - box.top - STICKY_SIZE.height / 2),
        }
      : { x: 24, y: 24 };

    // Offset a little per note, so pressing the button twice does not put the
    // second one exactly on the first.
    const offset = (notes.length % 6) * 22;

    await createSticky.mutateAsync({
      x: Math.round(middle.x + offset),
      y: Math.round(middle.y + offset),
      color: STICKY_COLORS[notes.length % STICKY_COLORS.length]!.value,
    });
  }

  const canvasHeight =
    visible.reduce(
      (lowest, w) =>
        Math.max(lowest, layout!.cards[w].y + (layout!.cards[w].height ?? heights[w] ?? 240)),
      0,
    ) + 120;

  return (
    <div className="w-full">
      {/*
        The heading and the arrange panel keep the same measure as every other
        page — they are text, and text does not want to be 2500px wide. Only
        the board below them uses the full screen, because that is a pinboard
        and every pixel of it is somewhere a card could go.
      */}
      <div className="mx-auto max-w-6xl">
        <header className="page-header flex flex-wrap items-end justify-between gap-3">
          <div>
            {/*
              No marks up here. The greeting is already the biggest thing on
              the page; a doodle either side of it was decoration on top of
              decoration. The one beside her name in the sidebar stays.
            */}
            <h1 className="text-2xl">
              {greeting()}, {firstName}
            </h1>
            <p className="mt-0.5 text-sm text-stone">
              {longDate()} · {DAY_LINE[zonedNow().getDay()]}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => void addSticky()}
              disabled={createSticky.isPending || notes.length >= MAX_STICKIES}
              title={
                notes.length >= MAX_STICKIES
                  ? `That is ${MAX_STICKIES} notes on the board. Anything more wants the Notes page.`
                  : 'A square of paper, dragged onto whatever it is about'
              }
              className="btn-soft"
            >
              {createSticky.isPending ? 'Adding…' : 'Add a note'}
            </button>

            {arranging && (
              <button
                onClick={tidyUp}
                title="Line the cards back up in even columns"
                className="btn-soft"
              >
                Tidy up
              </button>
            )}
            {/*
            A gear rather than the word "Arrange". Rearranging is a once-a-year
            job, and a full-sized button for it sat louder on the page than
            anything a teacher actually opens the dashboard to read. It turns
            into a plain "Done" while arranging, when it is the thing you want
            to find.
          */}
            {arranging ? (
              <button onClick={() => setArranging(false)} aria-pressed className="btn-primary">
                Done
              </button>
            ) : (
              <button
                onClick={() => setArranging(true)}
                aria-pressed={false}
                aria-label="Arrange the dashboard"
                title="Arrange the dashboard"
                className="rounded-lg p-2 text-clay transition-colors hover:bg-linen hover:text-terracotta"
              >
                <Icon name="settings" size={20} />
              </button>
            )}
          </div>
        </header>
      </div>

      {isLoading || !data ? (
        <p className="text-sm text-stone">Loading…</p>
      ) : (
        /*
         * A canvas, not a flow.
         *
         * Cards are positioned absolutely at coordinates the teacher chose,
         * so they stay exactly where they were put. The canvas is only the
         * area right of the sidebar and below the greeting, which is what
         * keeps a card from ever being dropped on top of either — there is no
         * rule against it, it simply is not part of the surface.
         */
        <>
          {/*
            Bringing a hidden card back.

            This used to be a panel above the board explaining how to drag,
            with a chip for every card — a permanent band of the screen spent
            repeating what the cards already say on themselves. What was
            actually needed from it is one row, only while arranging, and only
            when something is hidden: the moment there is nothing to bring
            back it disappears and the whole width is board again.
          */}
          {arranging && hidden.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-stone">Hidden:</span>
              {hidden.map((w) => (
                <button key={w} onClick={() => update(w, { hidden: false })} className="btn-soft">
                  + {WIDGET_LABELS[w]}
                </button>
              ))}
            </div>
          )}

          <div ref={canvasRef} className="relative" style={{ height: canvasHeight }}>
            {layout && visible.length === 0 && !arranging && (
              <div className="card max-w-lg">
                <p className="text-sm text-walnut">
                  Every card is hidden. Open the gear to bring one back.
                </p>
              </div>
            )}

            {visible.map((w) => {
              const card = layout!.cards[w];
              // A board saved on a wide monitor should not hang off the edge of
              // a laptop. Clamped on the way in, not just while dragging.
              const width = Math.min(card.width, canvasWidth);

              // How far this card can grow before it meets a neighbour.
              const others = visible.filter((o) => o !== w).map((o) => rectOf(layout!, o));
              const rect = rectOf(layout!, w);
              return (
                <WidgetFrame
                  key={w}
                  box={{
                    x: Math.min(card.x, Math.max(0, canvasWidth - width)),
                    y: card.y,
                    width,
                    height: card.height,
                  }}
                  accent={widgetColors()[w]}
                  arranging={arranging}
                  locked={card.locked}
                  bounds={{
                    width: canvasWidth,
                    maxWidth: maxWidthAt(rect, others, canvasWidth, GAP),
                    maxHeight: maxHeightAt(rect, others, GAP),
                  }}
                  zIndex={layout!.stack.indexOf(w) + 1}
                  onChange={(next) => update(w, next)}
                  onToggleLock={() => update(w, { locked: !card.locked })}
                  onHide={() => update(w, { hidden: true })}
                  onFit={() => update(w, { height: null })}
                  onRaise={() => raise(w)}
                  onMeasure={(height) => measureCard(w, height)}
                >
                  <WidgetBody widget={w} data={data} today={today} />
                </WidgetFrame>
              );
            })}

            {/*
              The notes, over everything.

              Inside the canvas rather than over the page, so a note keeps the
              same coordinates as the cards — stick one to the weather and it
              stays on the weather when the board scrolls. The layer itself is
              transparent to the mouse; only the squares of paper are not.
            */}
            <StickyLayer board={{ width: canvasWidth, height: canvasHeight }} />
          </div>
        </>
      )}
    </div>
  );
}

function WidgetBody({
  widget,
  data,
  today,
}: {
  widget: WidgetKey;
  data: DashboardResponse;
  today: string;
}) {
  const accent = widgetColors()[widget];
  return (
    <>
      {widget === 'plan' && <PlanWidget data={data} accent={accent} />}
      {widget === 'events' && <EventsWidget data={data} accent={accent} />}
      {widget === 'todos' && <TodosWidget data={data} accent={accent} />}
      {widget === 'habits' && <HabitsWidget data={data} today={today} accent={accent} />}
      {widget === 'inbox' && <InboxCard accent={accent} />}
      {widget.startsWith('weather') && <WeatherCard slot={widget} accent={accent} />}
      {widget === 'data' && <DataWidget data={data} accent={accent} />}
      {widget === 'links' && <LinksWidget data={data} accent={accent} />}
      {widget === 'jobs' && <JobsWidget accent={accent} />}
      {widget === 'documents' && <DocumentsWidget accent={accent} />}
    </>
  );
}

/**
 * A card heading.
 *
 * The glyph is the very same one the sidebar draws for that section — not a
 * lookalike — so "Today's lesson plan" and "Lesson plans" can never end up
 * wearing different pictures. Cards with no section of their own fall back to
 * a hand-drawn mark.
 */
function Head({
  title,
  to,
  action,
  accent,
  icon,
  doodle,
}: {
  title: string;
  to?: string;
  action?: string;
  accent: NavColor;
  /** A name from the sidebar's icon set. */
  icon?: string;
  doodle?: DoodleName;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className="flex items-center gap-2 text-base" style={{ color: accent.deep }}>
        {icon ? (
          <span style={{ color: accent.base }}>
            <Icon name={icon} />
          </span>
        ) : (
          doodle && <Doodle name={doodle} style={{ color: accent.base }} />
        )}
        {title}
      </h2>
      {to && action && (
        <Link
          to={to}
          className="text-[11px] transition-colors hover:underline"
          // Words, so the readable shade rather than the raw accent — and at
          // 11px it needs it more than anything else on the card.
          style={{ color: accent.label }}
        >
          {action}
        </Link>
      )}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="relative text-sm leading-relaxed text-stone">{children}</p>;
}

function PlanWidget({ data, accent }: { data: DashboardResponse; accent: NavColor }) {
  const { rows, cells } = data.lessonPlan;

  return (
    <>
      <Head
        title="Today's lesson plan"
        to="/lesson-plans"
        action="Full week"
        accent={accent}
        icon="book"
      />
      {rows.length === 0 ? (
        <>
          <Empty>Nothing planned for today — a blank page is a fine place to start.</Empty>
          <EmptyMark name="pencil" color={accent.base} />
        </>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => {
            const cell = cells[row.id];

            return (
              <li key={row.id} className="border-l-2 pl-3" style={{ borderColor: accent.tint }}>
                <p className="text-sm text-espresso">
                  {row.label}
                  {row.timeStart && (
                    <span className="ml-2 text-[11px] text-stone">
                      {row.timeStart}
                      {row.timeEnd ? `–${row.timeEnd}` : ''}
                    </span>
                  )}
                  {/*
                    Said rather than hidden. Specials and lunch are part of the
                    day and belong on the card; what they are not is something
                    she is teaching, and the line says so instead of the row
                    being dropped altogether.
                  */}
                  {row.noStudents && (
                    <span className="ml-2 text-[11px] text-stone">students are elsewhere</span>
                  )}
                </p>

                {cellIsEmpty(cell) ? (
                  <p className="mt-0.5 text-xs text-stone">Nothing written for this yet.</p>
                ) : cellMode(cell) === 'simple' ? (
                  /*
                    A simple cell is one block of writing with no field
                    headings. Reading it through PLAN_FIELDS — as this did
                    until now — found nothing at all, so a day planned in
                    simple mode showed as a list of subject names with the
                    writing invisible.
                  */
                  <p
                    className="rich-text-view mt-0.5 text-xs leading-snug text-walnut"
                    // Sanitised server-side on write; see
                    // server/src/services/sanitizeHtml.ts
                    dangerouslySetInnerHTML={{ __html: cell?.plan ?? '' }}
                  />
                ) : (
                  PLAN_FIELDS.map((field) => {
                    const codes =
                      field === 'standards' ? standardCodesLabel(cell?.standards) : null;
                    if (field === 'standards' ? !codes : !stripHtml(cell?.[field])) return null;

                    return (
                      <p key={field} className="mt-0.5 text-xs leading-snug">
                        <span className="text-stone">{PLAN_FIELD_LABELS[field]}: </span>
                        {codes !== null ? (
                          <span className="text-terracotta">{codes}</span>
                        ) : (
                          <span
                            className="rich-text-view inline text-walnut"
                            // Sanitised server-side on write; see
                            // server/src/services/sanitizeHtml.ts
                            dangerouslySetInnerHTML={{ __html: cell?.[field] ?? '' }}
                          />
                        )}
                      </p>
                    );
                  })
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function EventsWidget({ data, accent }: { data: DashboardResponse; accent: NavColor }) {
  return (
    <>
      <Head
        title="Today's events"
        to="/calendar"
        action="Calendar"
        accent={accent}
        icon="calendar"
      />
      {data.events.length === 0 ? (
        <>
          <Empty>Nothing on the calendar. Enjoy the quiet.</Empty>
          <EmptyMark name="coffee" color={accent.base} />
        </>
      ) : (
        <ul className="space-y-2">
          {data.events.map((e) => (
            <li
              key={e.id}
              className="border-l-[3px] pl-2.5"
              style={{ borderLeftColor: eventColor(e) }}
            >
              <div className="text-sm leading-tight text-espresso">{e.title}</div>
              <div className="text-[11px] text-stone">
                {e.allDay ? 'All day' : minutesRangeLabel(e.startMinutes, e.endMinutes)}
                {e.location ? ` · ${e.location}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * One line of the to-do card, with a way to get rid of it.
 *
 * The card could add but not remove, which made it a one-way inbox: type
 * something in by mistake and the only way out was to go to the To-do page
 * and find it there.
 *
 * A repeating to-do asks first, because "delete" means three different things
 * for one of those — not this week, not any more, or never at all — and
 * silently picking one on a teacher's behalf is how a Friday reminder
 * disappears for the rest of the year.
 */
function TodoRow({ todo }: { todo: Todo }) {
  const update = useUpdateTodo();
  const remove = useDeleteTodo();
  const stopRepeating = useStopRepeating();

  const repeating = todo.kind === 'weekly';
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);

  return (
    <li
      className={[
        'group flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors',
        todo.completed ? 'bg-sage/20 text-olive line-through' : 'hover:bg-linen',
      ].join(' ')}
    >
      <input
        type="checkbox"
        checked={todo.completed}
        onChange={(e) =>
          update.mutate({ id: todo.id, week: todo.weekStart, completed: e.target.checked })
        }
        aria-label={`Mark "${todo.text}" ${todo.completed ? 'not done' : 'done'}`}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-olive"
      />

      <span className="min-w-0 flex-1 leading-snug">
        {todo.text}
        {repeating && (
          <span className="ml-1.5 text-[11px] text-clay" title="Repeats every week">
            ↻
          </span>
        )}
      </span>

      <button
        onClick={(e) => {
          if (!repeating) {
            remove.mutate({ id: todo.id, week: todo.weekStart, scope: 'series' });
            return;
          }
          const box = e.currentTarget.getBoundingClientRect();
          setMenuAt({ x: box.right, y: box.bottom });
        }}
        aria-label={`Remove "${todo.text}"`}
        title="Remove"
        className="mt-0.5 shrink-0 text-stone opacity-0 transition-opacity hover:text-rust focus:opacity-100 group-hover:opacity-100"
      >
        ✕
      </button>

      {menuAt && (
        /*
         * Portalled and positioned from the button's own rectangle. A card is
         * absolutely positioned with a z-index, which makes it a stacking
         * context — a menu rendered inside one is painted under the card
         * beside it, however high its z-index goes.
         */
        <Portal>
          <div className="fixed inset-0 z-[80]" onClick={() => setMenuAt(null)}>
            <div
              className="absolute w-56 rounded-lg border border-sand bg-white py-1 text-xs shadow-lifted"
              style={{ left: Math.max(8, menuAt.x - 224), top: menuAt.y + 4 }}
              onClick={(e) => e.stopPropagation()}
              role="menu"
            >
              <p className="px-3 py-1 text-[11px] text-stone">This one repeats every week.</p>

              <button
                className="block w-full px-3 py-1.5 text-left text-walnut hover:bg-linen"
                onClick={() => {
                  remove.mutate({ id: todo.id, week: todo.weekStart, scope: 'week' });
                  setMenuAt(null);
                }}
              >
                Skip just this week
              </button>
              <button
                className="block w-full px-3 py-1.5 text-left text-walnut hover:bg-linen"
                onClick={() => {
                  stopRepeating.mutate({ id: todo.id, week: todo.weekStart });
                  setMenuAt(null);
                }}
              >
                Stop repeating after this week
              </button>
              <button
                className="block w-full px-3 py-1.5 text-left text-rust hover:bg-linen"
                onClick={() => {
                  remove.mutate({ id: todo.id, week: todo.weekStart, scope: 'series' });
                  setMenuAt(null);
                }}
              >
                Delete every week
              </button>
            </div>
          </div>
        </Portal>
      )}
    </li>
  );
}

function TodosWidget({ data, accent }: { data: DashboardResponse; accent: NavColor }) {
  const create = useCreateTodo();
  const [text, setText] = useState('');
  const remaining = data.todos.filter((t) => !t.completed).length;

  /**
   * Adds to whichever day this card is showing.
   *
   * The card knows the weekday and the week it is displaying, so a quick add
   * lands on the right day without asking — including on a Saturday, when the
   * card is already showing Monday and that is plainly where the thing
   * belongs.
   */
  function add() {
    const trimmed = text.trim();
    if (!trimmed) return;

    create.mutate({ text: trimmed, weekday: data.weekday, weekStart: data.weekStart });
    setText('');
  }

  return (
    <>
      <Head
        title={data.isWeekend ? 'Coming up Monday' : "Today's to-do"}
        to="/todos"
        action={remaining === 0 ? 'All done' : `${remaining} left`}
        accent={accent}
        icon="check"
      />

      {data.todos.length === 0 ? (
        <>
          <Empty>Nothing on the list for {WEEKDAY_LABELS[data.weekday]}. Well played.</Empty>
          <EmptyMark name="sun" color={accent.base} />
        </>
      ) : (
        <ul className="space-y-0.5">
          {data.todos.map((t) => (
            <TodoRow key={t.id} todo={t} />
          ))}
        </ul>
      )}

      {/*
        Quick add, always there.
        
        Adding something for today meant opening the to-do page, which is a
        page away from the board it belongs on. It stays below the list rather
        than above it, so the list does not jump every time something is
        ticked off.
      */}
      <div className="mt-2 flex gap-1.5">
        <input
          className="field py-1 text-sm"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
            if (e.key === 'Escape') setText('');
          }}
          placeholder={`Add to ${WEEKDAY_LABELS[data.weekday]}…`}
          aria-label={`Add a to-do for ${WEEKDAY_LABELS[data.weekday]}`}
          maxLength={200}
        />
        <button
          onClick={add}
          disabled={!text.trim() || create.isPending}
          className="btn-soft-accent shrink-0"
        >
          Add
        </button>
      </div>
    </>
  );
}

function HabitsWidget({
  data,
  today,
  accent,
}: {
  data: DashboardResponse;
  today: string;
  accent: NavColor;
}) {
  return (
    // A flex column so the tracker can measure the height it actually has and
    // grow its squares into it. A plain fragment leaves it measuring the whole
    // card, heading included, and overflowing by exactly the heading.
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">
        <Head title="Habit tracker" accent={accent} doodle="star" />
      </div>
      <HabitTracker
        initial={data.habits}
        todayYear={Number(today.slice(0, 4))}
        todayMonth={Number(today.slice(5, 7))}
        todayDay={Number(today.slice(8, 10))}
      />
    </div>
  );
}

function DataWidget({ data, accent }: { data: DashboardResponse; accent: NavColor }) {
  return (
    <>
      <Head
        title="Assessment progress"
        to="/data"
        action="Open data"
        accent={accent}
        icon="chart"
      />
      {data.dataSnapshot.length === 0 ? (
        <>
          <Empty>No assessments set up yet.</Empty>
          <EmptyMark name="ruler" color={accent.base} />
        </>
      ) : (
        <ul className="space-y-2.5">
          {data.dataSnapshot.map((s) => {
            const pct = s.total ? Math.round((s.scored / s.total) * 100) : 0;
            const done = s.total > 0 && s.scored >= s.total;

            return (
              <li key={s.assessmentId}>
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate text-espresso">
                    {s.name}
                    {s.term && <span className="ml-1 text-stone">{s.term}</span>}
                  </span>
                  <span className={done ? 'text-olive' : 'text-stone'}>
                    {s.scored} of {s.total}
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-linen">
                  <div
                    className="h-full rounded-full transition-[width]"
                    style={{ width: `${pct}%`, backgroundColor: done ? '#7C8A5F' : '#B07C63' }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function WeatherCard({ slot, accent }: { slot: string; accent: NavColor }) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">
        <Head title="Weather" accent={accent} doodle="sun" />
      </div>
      <WeatherWidget slot={slot} accent={accent} />
    </div>
  );
}

function InboxCard({ accent }: { accent: NavColor }) {
  return (
    <>
      <Head title="Inbox" accent={accent} icon="note" />
      <InboxWidget accent={accent} />
    </>
  );
}

/**
 * Whose job is whose, without opening the page.
 *
 * Both clocks on one card, because on a Monday morning what she needs is the
 * line leader (a month) and the trash helper (a week) in the same glance. The
 * counts and the flags are left on the jobs page: this is the answer to
 * "whose turn is it", not the place to change it.
 *
 * Every job, filled or not. The card used to list only the ones with somebody
 * in them, which meant a job she had just invented did not appear at all — so
 * the card looked broken rather than looking like there was a job to fill.
 * Vacant is a fact worth showing; it is the one that needs doing something
 * about.
 */
function JobsWidget({ accent }: { accent: NavColor }) {
  const { data: students = [] } = useStudents();
  const weekly = useJobBoard('weekly', periodOf('weekly'));
  const monthly = useJobBoard('monthly', periodOf('monthly'));

  const byId = useMemo(() => new Map(students.map((s) => [s.id, s])), [students]);

  /*
   * Whose name goes beside a job, built from the roster rather than from the
   * count of holders. A child who has left the class leaves a turn behind
   * pointing at nobody, and a job that reads as taken with no name beside it
   * is worse than one that plainly says vacant.
   */
  const rowsOf = (jobs: { id: string; name: string; holders: { studentId: string }[] }[]) =>
    jobs.map((job) => ({
      id: job.id,
      name: job.name,
      who: job.holders
        .map((h) => {
          const student = byId.get(h.studentId);
          return student ? studentName(student) : null;
        })
        .filter(Boolean)
        .join(', '),
    }));

  const groups = [
    { label: 'This month', rows: rowsOf(monthly.data?.jobs ?? []) },
    { label: 'This week', rows: rowsOf(weekly.data?.jobs ?? []) },
  ].filter((group) => group.rows.length > 0);

  return (
    <>
      <Head title="Classroom jobs" to="/jobs" action="Open" accent={accent} icon="badge" />

      {groups.length === 0 ? (
        <>
          <Empty>No jobs yet. Open adds the first one.</Empty>
          <EmptyMark name="star" color={accent.base} />
        </>
      ) : (
        <div className="space-y-2.5">
          {groups.map((group) => (
            <div key={group.label}>
              {/*
                Said, because the two clocks look identical in a list and the
                difference matters: the trash helper changes on Monday and the
                line leader does not.
              */}
              <p className="mb-0.5 text-[11px] uppercase tracking-wide text-stone">{group.label}</p>

              <ul className="space-y-1">
                {group.rows.map((job) => (
                  <li key={job.id} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="shrink-0 text-walnut">{job.name}</span>

                    {/*
                      Wrapped rather than clipped.

                      A job two children share was being cut to "Mariia,
                      Khyrie, S…", and half a name is worse than no name — it
                      is the one thing the card exists to tell her, and she
                      would have to open the page to finish reading it. The
                      row grows to a second line instead; the card is as tall
                      as it needs to be either way.
                    */}
                    {job.who ? (
                      <span className="min-w-0 break-words text-right text-espresso">
                        {job.who}
                      </span>
                    ) : (
                      <span className="shrink-0 text-right italic text-stone">Vacant</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * The documents she pins, a click from the printer.
 *
 * Only the pinned ones. A card listing forty forms is a filing cabinet on the
 * dashboard; the point of pinning something is that it is one of the three she
 * prints most weeks.
 */
function DocumentsWidget({ accent }: { accent: NavColor }) {
  const { data } = useDocuments();
  const pinned = (data?.documents ?? []).filter((d) => d.pinned);

  return (
    <>
      <Head title="Pinned documents" to="/documents" action="All of them" accent={accent} icon="folder" />

      {pinned.length === 0 ? (
        <>
          <Empty>Pin a document and it waits here.</Empty>
          <EmptyMark name="paperclip" color={accent.base} />
        </>
      ) : (
        <ul className="space-y-1">
          {pinned.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 flex-1 truncate text-espresso">{doc.name}</span>

              {canPrintInBrowser(doc.contentType) ? (
                <button
                  className="btn-soft shrink-0"
                  onClick={() => void printDocuments([{ url: documentUrl(doc), contentType: doc.contentType }])}
                >
                  Print
                </button>
              ) : (
                <a className="btn-soft shrink-0" href={documentDownloadUrl(doc)}>
                  Download
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function LinksWidget({ data, accent }: { data: DashboardResponse; accent: NavColor }) {
  return (
    <>
      <Head title="Quick links" to="/bookmarks" action="Manage" accent={accent} icon="bookmark" />
      {data.quickLinks.length === 0 ? (
        <>
          <Empty>Star a bookmark to pin it here.</Empty>
          <EmptyMark name="paperclip" color={accent.base} />
        </>
      ) : (
        <div className="flex flex-wrap gap-2.5">
          {data.quickLinks.map((b) => (
            <a key={b.id} href={b.url} target="_blank" rel="noopener noreferrer" className="group">
              <BookmarkTile bookmark={b} size="small" />
            </a>
          ))}
        </div>
      )}
    </>
  );
}
